import type { DrawingModule } from "../types/DrawingWeb";
import type { DrawingModuleType } from "../DrawingSingleton";
import { deleteObj } from "../core/delete";
import { OpenMode } from "../core/constants";
import { getCurrentSpaceId, openAs } from "../core/oda";

/**
 * Hex handle of an entity, as a BigInt (handles grow with creation order).
 */
function handleValue(entityId: DrawingModule.OdDbObjectId): bigint {
  const handle = entityId.getHandle();
  const ascii = handle.ascii();
  const str = ascii.toString();
  deleteObj(ascii);
  deleteObj(handle);
  try {
    return BigInt(`0x${str}`);
  } catch {
    return BigInt(0);
  }
}

/**
 * Sort entity IDs by draw order (first drawn first, topmost last).
 *
 * Uses the current space's SortentsTable (same notion as AutoCAD's DRAWORDER,
 * e.g. images are pushed to the bottom by ImageManager). When the space has no
 * SortentsTable yet, the draw order is the creation order, i.e. the handle order.
 * The table is never created here: that would mark the drawing as modified.
 */
function sortByDrawOrder(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  ids: DrawingModule.OdDbObjectId[],
): DrawingModule.OdDbObjectId[] {
  let space: DrawingModule.OdDbBlockTableRecord | null = null;
  let sortents: DrawingModule.OdDbSortentsTable | null = null;
  const idArray = new module.OdDbObjectIdArray();
  try {
    space = openAs(getCurrentSpaceId(db), module.OdDbBlockTableRecord, OpenMode.kForRead);
    sortents = space.getSortentsTable(false);
    if (sortents) {
      ids.forEach((id) => idArray.push_back(id));
      sortents.getRelativeDrawOrder(idArray, 0);
      const sorted: DrawingModule.OdDbObjectId[] = [];
      for (let i = 0; i < idArray.size(); i++) sorted.push(idArray.get(i));
      if (sorted.length === ids.length) return sorted;
    }
  } catch {
    // No usable SortentsTable → handle order below.
  } finally {
    deleteObj(idArray);
    if (sortents) deleteObj(sortents);
    if (space) deleteObj(space);
  }

  const values = new Map(ids.map((id) => [id, handleValue(id)]));
  return [...ids].sort((a, b) => {
    const va = values.get(a)!;
    const vb = values.get(b)!;
    return va < vb ? -1 : va > vb ? 1 : 0;
  });
}

/**
 * Reduce a selection set to its topmost entity (the one drawn last), so that a
 * single click always selects exactly one entity even when several overlap the
 * pick aperture. Returns a new set and releases the given one; null if empty.
 */
export function keepTopmostEntity(
  module: DrawingModuleType,
  db: DrawingModule.OdDbDatabase,
  selectionSet: DrawingModule.OdDbSelectionSet | null,
): DrawingModule.OdDbSelectionSet | null {
  if (!selectionSet) return null;

  const idArray = selectionSet.objectIdArray();
  const count = idArray.size();
  if (count === 1) return selectionSet;
  if (count === 0) {
    deleteObj(selectionSet);
    return null;
  }

  const ids: DrawingModule.OdDbObjectId[] = [];
  for (let i = 0; i < count; i++) ids.push(idArray.get(i));
  const topmost = sortByDrawOrder(module, db, ids).at(-1)!;

  const single = module.OdDbSelectionSet.createObject1(db);
  single.append(topmost, selectionSet.method(topmost));
  deleteObj(selectionSet);
  return single;
}
